<script setup>
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlusSmall from '@/components/icon/IconPlusSmall.vue'
import IconLock from '@/components/icon/IconLock.vue'
import { DOCK_ADD_BUTTON } from './mediaflowMeta'

/**
 * One OBS-style dock: title, count badge and "+ Add" in a fixed-height
 * header (the same in every dock, so the four line up), then a scrolling
 * list body. The "Add" label drops to icon-only when the dock is narrow
 * (container query on `.mf-dock`).
 */
defineProps({
	title: { type: String, required: true },
	/** Small count badge next to the title, e.g. "3" or "2/10". Context goes in the body, not here. */
	count: { type: String, default: '' },
	countLabel: { type: String, default: '' },
	testid: { type: String, default: '' },
	/** Label for the default "+" button (also its tooltip). Use the `add` slot for a menu instead. */
	addLabel: { type: String, default: '' },
	addDisabledReason: { type: String, default: '' },
	/** Feature not available on this engine: content stays readable, a lock note explains why. */
	locked: { type: Boolean, default: false },
	lockedReason: { type: String, default: '' },
})

const emit = defineEmits(['add'])
</script>

<template>
	<section
		class="mf-dock flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl bg-surface-2 ring-1 ring-surface-3"
		:aria-label="title"
		:data-testid="testid || undefined"
		:data-locked="locked ? 'true' : undefined"
	>
		<header class="flex h-10 shrink-0 items-center gap-2 pl-3 pr-1.5">
			<h2 class="m-0 shrink-0 text-xs font-semibold uppercase tracking-wider text-surface-text-1">{{ title }}</h2>
			<span v-if="count" class="inline-flex h-5 shrink-0 items-center rounded-full bg-surface-3 px-1.5 text-[0.65rem] font-semibold tabular-nums text-surface-8" :title="countLabel || undefined" :data-testid="testid ? `${testid}-count` : undefined">{{ count }}</span>
			<Tooltip v-if="locked" :content="lockedReason" wrapper-class="inline-flex" popper-class="max-w-[240px] text-sm break-words">
				<span class="inline-flex size-5 items-center justify-center rounded text-surface-7" :aria-label="lockedReason" :data-testid="testid ? `${testid}-locked` : undefined">
					<icon-base width="11" height="11" fill="none"><icon-lock /></icon-base>
				</span>
			</Tooltip>
			<span class="ml-auto flex shrink-0 items-center gap-1">
				<slot name="header-extra" />
				<slot name="add">
					<Tooltip v-if="addLabel" :content="addDisabledReason || addLabel" wrapper-class="inline-flex" wrap-trigger>
						<button
							type="button"
							:class="[DOCK_ADD_BUTTON, 'focus:outline-none focus-visible:ring-2 focus-visible:ring-main disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent']"
							:disabled="!!addDisabledReason"
							:aria-label="addLabel"
							:data-testid="testid ? `${testid}-add` : undefined"
							@click="emit('add')"
						>
							<icon-base width="11" height="11" :width-view-box="14" :height-view-box="14"><icon-plus-small /></icon-base>
							<span class="mf-dock-add-label">Add</span>
						</button>
					</Tooltip>
				</slot>
			</span>
		</header>
		<p v-if="$slots.context" class="m-0 -mt-1 shrink-0 truncate px-3 pb-1 text-[0.7rem] text-surface-7" :data-testid="testid ? `${testid}-context` : undefined"><slot name="context" /></p>
		<!-- Padding on every side so row rings are never clipped by the scroller -->
		<div class="min-h-0 flex-1 overflow-y-auto p-1.5">
			<slot />
		</div>
		<slot name="footer" />
	</section>
</template>

<style>
/* Narrow docks (≈ 1280 px with the app nav): "+ Add ▾" becomes "+". */
.mf-dock {
	container-type: inline-size;
}
/* Dock rows: soft cards a shade above the dock, no divider lines. Rows with a
   tally (on air / preview / selected) set data-tone and use their own colours. */
.mf-row {
	transition: background-color 0.15s ease, box-shadow 0.15s ease;
}
.mf-row:not([data-tone]) {
	background-color: color-mix(in srgb, var(--surface-3) 38%, var(--surface-2));
}
.mf-row:not([data-tone]):hover {
	background-color: color-mix(in srgb, var(--surface-3) 70%, var(--surface-2));
}
/* Small inline fields inside docks: own background + ring on any row colour. */
.mf-input {
	height: 1.75rem;
	border: 0;
	border-radius: 0.375rem;
	background-color: var(--surface-1);
	box-shadow: inset 0 0 0 1px var(--surface-4);
	padding: 0 0.5rem;
	font-size: 0.75rem;
	color: var(--surface-text-2);
	outline: none;
	transition: box-shadow 0.15s ease;
}
.mf-input:hover:not(:disabled) {
	box-shadow: inset 0 0 0 1px var(--surface-6);
}
.mf-input:focus {
	box-shadow: inset 0 0 0 1px var(--c-main), 0 0 0 3px var(--c-main-transparent);
}
.mf-input:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}
@container (max-width: 250px) {
	.mf-dock .mf-dock-add-label {
		display: none;
	}
}
</style>
